import { CapacitorHttp } from '@capacitor/core'
import type { ShiftFix } from '~/utils/shiftLocation'

// This watcher belongs to the phone that opted in, even if the worker also signs in elsewhere.
export default defineNuxtPlugin(() => {
  const { session, token, fieldFetch } = useField()
  const status = useState<string>('shift-gps-status', () => '')
  const lastFix = useState<ShiftFix | null>('shift-gps-last-fix', () => null)
  const base = useRuntimeConfig().public.baseURL as string
  let active = '',
    revision = 0,
    release: (() => void) | undefined,
    sending = false
  let lastTime = 0,
    suppressed = ''
  const owner = () => session.value?.worker_id
  const prefix = (id: number) => 'field-clock-' + id + '/'
  const queue = (id: number) => 'field-gps-' + id + '/'
  function desired() {
    const id = owner()
    if (
      !id ||
      !token.value ||
      token.value !== localStorage.getItem('field-token') ||
      Date.parse(session.value!.expires_at) <= Date.now()
    )
      return ''
    const p = prefix(id),
      consent = localStorage.getItem(p + 'gps-opt-in')
    if (!consent) return ''
    const raw = localStorage.getItem(p + 'state')
    if (!raw) return ''
    const events: PendingPunch[] = Object.keys(localStorage)
      .filter((k) => k.startsWith(p + 'event/'))
      .map((k) => JSON.parse(localStorage.getItem(k)!))
      .sort(
        (a, b) =>
          a.occurred_at.localeCompare(b.occurred_at) ||
          a.client_id.localeCompare(b.client_id)
      )
    const { current } = projectClock(JSON.parse(raw), events)
    const last = current[current.length - 1]
    return current[0]?.client_id === consent &&
      current[0].track_location &&
      last &&
      ['in', 'resume'].includes(last.action)
      ? id + '/' + consent + '/' + last.client_id
      : ''
  }
  async function flush() {
    const id = owner(),
      auth = token.value
    if (
      sending ||
      !id ||
      !auth ||
      !navigator.onLine ||
      auth !== localStorage.getItem('field-token')
    )
      return
    sending = true
    try {
      const q = queue(id)
      const jobs = new Map<string, string[]>()
      for (const k of Object.keys(localStorage)
        .filter((k) => k.startsWith(q))
        .sort()) {
        const shift = k.slice(q.length).split('/')[0]!
        if (!jobs.has(shift)) jobs.set(shift, [])
        jobs.get(shift)!.push(k)
      }
      for (const [shift, keys] of jobs) {
        for (let i = 0; i < keys.length; i += 100) {
          if (
            token.value !== auth ||
            owner() !== id ||
            localStorage.getItem('field-token') !== auth
          )
            return
          const batch = keys
            .slice(i, i + 100)
            .filter((k) => localStorage.getItem(k))
          if (!batch.length) continue
          const body = {
            worker_id: id,
            shift_id: shift,
            points: batch.map((k) => JSON.parse(localStorage.getItem(k)!)),
          }
          let result: { active: boolean }
          try {
            if (shiftNative()) {
              const url = new URL(
                base.replace(/\/$/, '') + '/field/clock/locations/',
                location.origin
              )
              const r = await CapacitorHttp.post({
                url: url.href,
                headers: {
                  Authorization: 'Field ' + auth,
                  'Content-Type': 'application/json',
                  Origin: location.origin,
                },
                data: body,
              })
              if (r.status !== 200)
                throw Object.assign(new Error('GPS upload failed'), {
                  status: r.status,
                })
              result = r.data
            } else
              result = await fieldFetch('/clock/locations/', {
                method: 'POST',
                body,
              })
          } catch (e) {
            const code = (e as { status?: number }).status
            if (
              code === 409 &&
              !Object.keys(localStorage).some(
                (k) => k === prefix(id) + 'event/' + shift
              ) &&
              localStorage.getItem(prefix(id) + 'gps-opt-in') !== shift
            )
              batch.forEach((k) => localStorage.removeItem(k))
            if (code === 403 && owner() === id)
              batch.forEach((k) => localStorage.removeItem(k))
            break
          }
          batch.forEach((k) => localStorage.removeItem(k))
          if (
            !result.active &&
            active.startsWith(id + '/' + shift + '/') &&
            !Object.keys(localStorage).some((k) =>
              k.startsWith(prefix(id) + 'event/')
            )
          ) {
            suppressed = active
            refresh()
          }
        }
      }
    } finally {
      sending = false
    }
  }
  function refresh() {
    const next = desired()
    if (suppressed && next !== suppressed) suppressed = ''
    const target = next === suppressed ? '' : next
    if (target === active) return
    ++revision
    release?.()
    release = undefined
    active = target
    lastTime = 0
    status.value = ''
    if (!target) return
    const [id, shift] = target.split('/')
    const guard = revision
    const receive = (
      fix?: FieldPosition & { time?: number | null },
      err?: Error
    ) => {
      if (guard !== revision || desired() !== target) {
        refresh()
        return
      }
      if (err || !fix) {
        status.value = 'GPS unavailable. Check location permission and signal.'
        return
      }
      const time = fix.time ?? Date.now()
      lastFix.value = { ...fix, time, shift: shift!, worker: Number(id) }
      if (time - lastTime < 30000) {
        status.value = navigator.onLine
          ? 'Shift GPS active · last fix ' +
            new Date(lastTime).toLocaleTimeString()
          : 'Shift GPS active · saved offline.'
        return
      }
      const uuid = newClientUuid()
      try {
        localStorage.setItem(
          queue(Number(id)) + shift + '/' + uuid,
          JSON.stringify({
            client_id: uuid,
            occurred_at: new Date(time).toISOString(),
            latitude: fix.latitude,
            longitude: fix.longitude,
            accuracy: Math.max(0, fix.accuracy),
          })
        )
        lastTime = time
        status.value = navigator.onLine
          ? 'Shift GPS active · last fix ' + new Date(time).toLocaleTimeString()
          : 'Shift GPS active · saved offline.'
        void flush()
      } catch {
        status.value = 'GPS storage is full or unavailable on this phone.'
      }
    }
    status.value = 'Shift GPS enabled · waiting for a location.'
    if (shiftNative()) {
      shiftGps
        .addWatcher(
          {
            requestPermissions: true,
            stale: false,
            distanceFilter: 10,
            backgroundTitle: 'SOS Field · Shift GPS',
            backgroundMessage:
              'Sharing your work route. GPS pauses on break and stops at clock-out.',
          },
          receive
        )
        .then(
          (id) => {
            const stop = () => {
              void shiftGps.removeWatcher({ id }).catch(() => {})
            }
            if (guard === revision) release = stop
            else stop()
          },
          (err) => receive(undefined, err)
        )
    } else if (navigator.geolocation) {
      const id = navigator.geolocation.watchPosition(
        (p) =>
          receive({
            latitude: p.coords.latitude,
            longitude: p.coords.longitude,
            accuracy: p.coords.accuracy,
            time: p.timestamp,
          }),
        () => receive(undefined, new Error('GPS unavailable')),
        { enableHighAccuracy: true, maximumAge: 0, timeout: 30000 }
      )
      release = () => navigator.geolocation.clearWatch(id)
    }
  }
  async function reconcile() {
    refresh()
    const id = owner(),
      auth = token.value
    if (
      id &&
      auth &&
      auth === localStorage.getItem('field-token') &&
      navigator.onLine &&
      localStorage.getItem(prefix(id) + 'gps-opt-in')
    ) {
      try {
        const s = await fieldFetch<ClockState>('/clock/')
        if (id === owner() && auth === token.value) {
          localStorage.setItem(
            prefix(id) + 'state',
            JSON.stringify({
              recent: s.recent.map(shortEntry),
              current: s.current.map(shortEntry),
            })
          )
          refresh()
        }
      } catch {
        /* Keep local punches when offline. */
      }
    }
    void flush()
  }
  watch([token, () => session.value?.worker_id], reconcile, { immediate: true })
  window.addEventListener('field-clock-changed', () => {
    refresh()
    void flush()
  })
  window.addEventListener('storage', refresh)
  window.addEventListener('online', reconcile)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void reconcile()
  })
  setInterval(() => {
    if (active || (!document.hidden && owner())) void reconcile()
  }, 60000)
})
